import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import apiAxios from "../../../../../app/api/apiAxios";
import ResultCode from "../../../../../data/enums/ResultCode";
import getServerErrorResourceName from "../../../../../data/apiErrors/apiErrors";
import Button, {ButtonType} from "../../../../ui/components/Button/Button";
import FormLayout from "../../../../ui/layouts/FormLayout/FormLayout";
import {useServerError} from "../../../../../hooks/useServerError";
import SettingForm from "./SettingForm";
import {toast} from "react-toastify";

const initialState = {
    id: 0,
    name: '',
    value: '',
    settingDescription: '',
    notes: ''
}

const Setting = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {id} = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [setting, setSetting] = useState(initialState);
    const [serverError, setServerError] = useServerError(null);

    useEffect(() => {
        (async () => await load(id))();
    }, [id]);

    const load = async (id) => {
        if (id && id > 0) {
            setServerError(null);
            setSetting(initialState);
            setIsLoading(true);

            const url = `${process.env.REACT_APP_BE_DOMAIN}/api/setting/getSetting?id=${id}`;

            try {
                const response = await apiAxios.get(url);

                if (response) {
                    if (response.status === 200 && response.data) {
                        if (response.data.resultCode === ResultCode.Success) {
                            setSetting(response.data);
                        } else {
                            setServerError(t(getServerErrorResourceName({
                                model: 'setting',
                                resultCode: response.data.resultCode
                            })));
                        }
                    }
                }

                setIsLoading(false);
            } catch (e) {
                if (e && e.message) {
                    setServerError(`${t(getServerErrorResourceName({
                        model: 'setting',
                        resultCode: ResultCode.ServerError
                    }))} Message: (${e.message})`);
                }
                setIsLoading(false);
            }
        }
    }

    const save = async (values, actions) => {
        try {
            setServerError(null);
            setIsLoading(true);
            actions.setSubmitting(true);

            const response = await apiAxios.post(`${process.env.REACT_APP_BE_DOMAIN}/api/setting/save`, {
                id: setting.id,
                name: values.name,
                value: values.value,
                settingDescription: values.settingDescription,
                notes: values.notes
            });

            if (response) {
                if (response.status === 200 && response.data) {
                    if (response.data.resultCode === ResultCode.Success) {
                        toast.success(t("setting.form.saved-label"));

                        if (response.data.description) {
                            if (!id || id <= 0) {
                                await load(+response.data.description);
                            }
                        }
                    } else {
                        setServerError(t(getServerErrorResourceName({
                            model: 'setting',
                            resultCode: response.data.resultCode,
                            description: response.data.description
                        })));
                    }
                }
            }

            setIsLoading(false);
            actions.setSubmitting(false);
        } catch (err) {
            if (err && err.message) {
                setServerError(`${t(getServerErrorResourceName({
                    model: 'setting',
                    resultCode: ResultCode.ServerError
                }))} Message: (${err.message})`);
            }
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    }

    return (
        <FormLayout header={<div className='default-form-layout-header'>{t("setting.form.setting-label")}</div>}
                    footer={(
                        <div className='default-form-layout-footer'>
                            <Button buttonType={ButtonType.Back}
                                    onClick={() => navigate(-1)}
                                    disabled={isLoading}>
                                {t("btn.back")}
                            </Button>
                            <Button buttonType={ButtonType.Default}
                                    form='setting-form'
                                    type='submit'
                                    disabled={isLoading}>
                                {t("btn.save")}
                            </Button>
                        </div>
                    )}
                    footerPosition='top'
                    isLoading={isLoading}>
            <div className='default-form-layout-container'>
                {serverError &&
                    <div className='server-error-container'>
                        <div>
                            {serverError}
                        </div>
                    </div>
                }
                <SettingForm {...setting} onSubmit={save} />
            </div>
        </FormLayout>
    );
};

export default Setting;